import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react'

import { cn } from '@/lib/utils'
import { SendIcon, StopIcon } from './icons'

const LINE_HEIGHT = 24
const MAX_ROWS = 6

/** The message box: grows to six lines, Enter sends, Shift+Enter breaks a line. */
export function Composer({
  disabled,
  streaming,
  autoFocus,
  onSend,
  onStop,
  className,
  style,
}: {
  disabled?: boolean
  streaming: boolean
  autoFocus?: boolean
  onSend: (text: string) => void
  onStop: () => void
  className?: string
  style?: React.CSSProperties
}) {
  const [value, setValue] = useState('')
  const textarea = useRef<HTMLTextAreaElement>(null)

  useLayoutEffect(() => {
    const node = textarea.current
    if (!node) return
    node.style.height = 'auto'
    node.style.height = `${Math.min(node.scrollHeight, LINE_HEIGHT * MAX_ROWS)}px`
  }, [value])

  useEffect(() => {
    if (autoFocus && !disabled) textarea.current?.focus()
  }, [autoFocus, disabled])

  const submit = () => {
    const text = value.trim()
    if (!text || disabled || streaming) return
    onSend(text)
    setValue('')
  }

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault()
      submit()
    }
  }

  const canSend = value.trim().length > 0 && !disabled && !streaming

  return (
    <div className={className} style={style}>
      <div
        className={cn(
          'flex items-end gap-1.5 rounded-[24px] border border-line bg-surface-muted py-1.5 pl-4 pr-1.5 transition-colors focus-within:border-line-strong',
          disabled && 'opacity-70',
        )}
      >
        <textarea
          ref={textarea}
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={onKeyDown}
          rows={1}
          disabled={disabled}
          placeholder={disabled ? 'Finish the action above first' : 'Ask or tell me what to do…'}
          aria-label="Message the assistant"
          className="my-[10px] max-h-[144px] min-h-[24px] w-full resize-none bg-transparent text-[16px] leading-6 text-ink outline-none placeholder:text-ink-faint disabled:cursor-not-allowed"
        />
        {streaming ? (
          <button
            type="button"
            onClick={onStop}
            aria-label="Stop"
            className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-ink text-oncolor transition-colors hover:bg-ink/90"
          >
            <StopIcon className="h-5 w-5" />
          </button>
        ) : (
          <button
            type="button"
            onClick={submit}
            disabled={!canSend}
            aria-label="Send"
            className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-ink text-oncolor transition-colors hover:bg-ink/90 disabled:bg-line-strong disabled:text-ink-faint"
          >
            <SendIcon className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  )
}
